/**
 * Job detail page — full-screen view with header and tabbed interface.
 * Phase 17: header (17.1), tabs (17.2–17.8), create modal (17.9).
 * Design tab shown for is_design_job jobs (admin-only content enforced inside DesignTab).
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import useJob from '../hooks/useJob';
import JobHeader from '../components/jobs/JobHeader';
import BomTab from '../components/jobs/tabs/BomTab';
import RfqTab from '../components/jobs/tabs/RfqTab';
import OrdersTab from '../components/jobs/tabs/OrdersTab';
import TimeTrackingTab from '../components/jobs/tabs/TimeTrackingTab';
import CostingTab from '../components/jobs/tabs/CostingTab';
import DocumentsTab from '../components/jobs/tabs/DocumentsTab';
import ChatTab from '../components/jobs/tabs/ChatTab';
import JobNotesTab from '../components/jobs/tabs/JobNotesTab';
import TodoTab from '../components/jobs/tabs/TodoTab';
import DesignTab from '../components/jobs/tabs/DesignTab';
import { LoadingSpinner } from '../components/common';

const BASE_TABS = [
  { key: 'bom', label: 'BOM' },
  { key: 'rfq', label: 'RFQ / Quotes' },
  { key: 'orders', label: "PO's / Receiving" },
  { key: 'time', label: 'Time Tracking' },
  { key: 'costing', label: 'Costing' },
  { key: 'notes', label: 'Job Notes' },
  { key: 'todos', label: 'To-Do' },
  { key: 'documents', label: 'Documents' },
  { key: 'chat', label: 'Chat' },
];

export default function JobDetailPage() {
  const { id } = useParams();
  const { job, loading, error, refresh } = useJob(id);
  const [activeTab, setActiveTab] = useState('bom');

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-red-600 text-lg">{error}</p>
        <button onClick={refresh} className="mt-2 text-blue-600 hover:text-blue-700 text-sm">
          Retry
        </button>
      </div>
    );
  }

  if (!job) return null;

  // Design tab appears first for design jobs
  const TABS = job.is_design_job
    ? [{ key: 'design', label: '✏️ Design', isDesign: true }, ...BASE_TABS]
    : BASE_TABS;

  return (
    <div className="space-y-4">
      <JobHeader job={job} onRefresh={refresh} />

      {/* Tab bar */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-0 -mb-px overflow-x-auto">
          {TABS.map((tab) => {
            const unread = tab.key === 'chat' ? (job.unread_message_count || 0) : 0;
            const isDesignTab = tab.key === 'design';
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`relative px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.key
                    ? isDesignTab
                      ? 'border-purple-600 text-purple-600'
                      : 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                    {unread}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'design' && <DesignTab job={job} onJobUpdated={refresh} />}
        {activeTab === 'bom' && <BomTab job={job} onRefresh={refresh} />}
        {activeTab === 'rfq' && <RfqTab job={job} onRefresh={refresh} />}
        {activeTab === 'orders' && <OrdersTab job={job} onRefresh={refresh} />}
        {activeTab === 'time' && <TimeTrackingTab job={job} onRefresh={refresh} />}
        {activeTab === 'costing' && <CostingTab job={job} onRefresh={refresh} />}
        {activeTab === 'notes' && <JobNotesTab job={job} />}
        {activeTab === 'todos' && <TodoTab job={job} />}
        {activeTab === 'documents' && <DocumentsTab job={job} onRefresh={refresh} />}
        {activeTab === 'chat' && <ChatTab job={job} />}
      </div>
    </div>
  );
}
